import { useState, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import LanguageSelector from '../LanguageSelector';
import { 
    Activity, LayoutDashboard, Users, CreditCard, FileText, 
    Settings, LogOut, Menu, X, Shield, Wallet, Receipt, Brain, Ticket
} from 'lucide-react';

const AdminLayout = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const { admin, logout } = useAdmin();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const navItems = [
        { to: '/admin/dashboard', icon: LayoutDashboard, label: t('admin.dashboard') },
        { to: '/admin/users', icon: Users, label: t('admin.users') },
        { to: '/admin/plans', icon: CreditCard, label: t('admin.plans') },
        { to: '/admin/coupons', icon: Ticket, label: t('admin.coupons') },
        { to: '/admin/payments', icon: Wallet, label: t('admin.payments') },
        { to: '/admin/transactions', icon: Receipt, label: t('admin.transactions') },
        { to: '/admin/reports', icon: FileText, label: t('admin.reports') },
        { to: '/admin/ai-settings', icon: Brain, label: t('admin.aiSettings') },
        { to: '/admin/settings', icon: Settings, label: t('admin.settings') },
    ];

    const NavItem = ({ to, icon: Icon, label, onClick }) => (
        <NavLink
            to={to}
            className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                ${isActive 
                    ? 'bg-teal-500/20 text-teal-400' 
                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'}
            `}
            onClick={onClick}
            data-testid={`nav-${to.split('/').pop()}`}
        >
            <Icon className="w-5 h-5" />
            <span className="font-medium">{label}</span>
        </NavLink>
    );

    const handleLogout = async () => {
        await logout();
        navigate('/admin/login');
    };

    const closeSidebar = useCallback(() => setSidebarOpen(false), []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="admin-layout">
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50 backdrop-blur-sm sticky top-0 z-40">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-lg font-bold text-white">{isRTL ? 'لوحة التحكم' : 'Admin'}</span>
                </div>
                <div className="flex items-center gap-2">
                    <LanguageSelector variant="minimal" />
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-white"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </Button>
                </div>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <aside className={`
                    fixed lg:sticky top-0 ${isRTL ? 'right-0' : 'left-0'} z-30 h-screen w-64 
                    bg-slate-800/80 backdrop-blur-sm ${isRTL ? 'border-l' : 'border-r'} border-slate-700
                    transform transition-transform duration-300 ease-in-out
                    ${sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}>
                    <div className="flex flex-col h-full">
                        {/* Logo */}
                        <div className="p-6 border-b border-slate-700 hidden lg:block">
                            <div className="flex items-center gap-3">
                                <img 
                                    src="/logo.svg" 
                                    alt={t('app.name')} 
                                    className="h-12 w-auto"
                                />
                                <div>
                                    <span className="text-lg font-bold text-white">{t('app.name')}</span>
                                    <span className="block text-xs text-slate-400">{isRTL ? 'بوابة المشرف' : 'Admin Portal'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Navigation */}
                        <nav className="flex-1 p-4 space-y-1 overflow-y-auto mt-16 lg:mt-0">
                            {navItems.map((item) => (
                                <NavItem key={item.to} {...item} onClick={closeSidebar} />
                            ))}
                        </nav>

                        {/* Admin Info & Logout */}
                        <div className="p-4 border-t border-slate-700">
                            <div className="flex items-center justify-between mb-4 px-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                                        <Shield className="w-5 h-5 text-teal-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-white truncate">{admin?.name}</p>
                                        <p className="text-xs text-slate-400 truncate">
                                            {admin?.role === 'super_admin' ? t('admin.superAdmin') : t('admin.admin')}
                                        </p>
                                    </div>
                                </div>
                                <div className="hidden lg:block">
                                    <LanguageSelector variant="minimal" />
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                                onClick={handleLogout}
                                data-testid="admin-logout-btn"
                            >
                                <LogOut className="w-5 h-5 ltr:mr-3 rtl:ml-3" />
                                {t('admin.logout')}
                            </Button>
                        </div>
                    </div>
                </aside>

                {/* Overlay for mobile */}
                {sidebarOpen && (
                    <div 
                        className="fixed inset-0 bg-black/50 z-20 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Content */}
                <main className="flex-1 min-h-screen lg:min-h-[calc(100vh)]">
                    <div className="p-4 lg:p-8">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
